import { useState, useMemo, useRef, useEffect } from 'react';
import { RefreshCw, Search, X, UserPlus, Star } from 'lucide-react';
import { usePagePermissions } from '../hooks/usePagePermissions';
import { useToast } from '../components/ui/Toast';
import { useSupplierPmConfig, type SupplierWithAssignments } from '../hooks/useSupplierPmConfig';
import type { EmployeeRow } from '../types/supplierPmConfig';

export function SupplierPmConfigPage() {
    const { canEdit, isLoading: permLoading } = usePagePermissions('/supplier-pm-config');
    const { toast } = useToast();
    const {
        employees, companyCodes, businessUnitCodes,
        selectedCompanyCode, setSelectedCompanyCode,
        selectedBusinessUnit, setSelectedBusinessUnit,
        searchTerm, setSearchTerm,
        filteredSuppliers,
        loading, error,
        assign, unassign, refresh,
    } = useSupplierPmConfig();

    const [openAddFor, setOpenAddFor] = useState<string | null>(null);
    const [busyKey, setBusyKey] = useState<string | null>(null);

    const handleAdd = async (s: SupplierWithAssignments, staffID: string) => {
        const key = `add-${s.SupplierDimID}-${staffID}`;
        setBusyKey(key);
        try {
            const isFirst = s.assignments.length === 0;
            await assign(s.CompanyCode, s.SupplierDimID, staffID, isFirst);
            toast(`Assigned to ${s.SupplierDimName || s.SupplierDimCode}`, 'success');
            setOpenAddFor(null);
        } catch (err) {
            toast(err instanceof Error ? err.message : 'Failed to assign', 'error');
        } finally {
            setBusyKey(null);
        }
    };

    const handleRemove = async (assignmentId: number, label: string) => {
        const key = `remove-${assignmentId}`;
        setBusyKey(key);
        try {
            await unassign(assignmentId);
            toast(`Removed ${label}`, 'success');
        } catch (err) {
            toast(err instanceof Error ? err.message : 'Failed to remove', 'error');
        } finally {
            setBusyKey(null);
        }
    };

    const handleSetPrimary = async (s: SupplierWithAssignments, staffID: string) => {
        const key = `primary-${s.SupplierDimID}-${staffID}`;
        setBusyKey(key);
        try {
            await assign(s.CompanyCode, s.SupplierDimID, staffID, true);
            toast('Primary updated', 'success');
        } catch (err) {
            toast(err instanceof Error ? err.message : 'Failed to update', 'error');
        } finally {
            setBusyKey(null);
        }
    };

    if (permLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Supplier Product Managers</h1>
                    <p className="mt-1 text-sm text-gray-500">
                        Assign Product Managers to suppliers per company. Only suppliers with sales since 2022-01-01 are shown.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={refresh}
                        disabled={loading}
                        className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                        <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </button>
                    {canEdit && (
                        <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                            Edit Access
                        </span>
                    )}
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-col gap-2 bg-white p-3 border border-gray-200 rounded-md">
                <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-700 w-24">Company:</span>
                        <div className="flex flex-wrap gap-1">
                            {companyCodes.map(code => (
                                <button
                                    key={code}
                                    onClick={() => setSelectedCompanyCode(code)}
                                    className={`px-2.5 py-1 text-xs font-medium rounded uppercase ${
                                        selectedCompanyCode === code
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                >
                                    {code}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-700 w-24">Business Unit:</span>
                        <div className="flex flex-wrap gap-1">
                            <button
                                onClick={() => setSelectedBusinessUnit(null)}
                                className={`px-2.5 py-1 text-xs font-medium rounded ${
                                    selectedBusinessUnit === null
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                All
                            </button>
                            {businessUnitCodes.map(code => (
                                <button
                                    key={code}
                                    onClick={() => setSelectedBusinessUnit(code)}
                                    className={`px-2.5 py-1 text-xs font-medium rounded ${
                                        selectedBusinessUnit === code
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                >
                                    {code}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="flex-1 relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        placeholder="Search supplier name or code..."
                        className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                </div>
            </div>

            {/* Error */}
            {error && (
                <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                    {error}
                </div>
            )}

            {/* Loading or list */}
            {loading ? (
                <div className="flex items-center justify-center h-40">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                </div>
            ) : (
                <div className="bg-white border border-gray-200 rounded-md">
                    {filteredSuppliers.length === 0 ? (
                        <div className="p-8 text-center text-sm text-gray-500">
                            No suppliers match the current filter.
                        </div>
                    ) : (
                        <div className="divide-y divide-gray-100">
                            <div className="px-4 py-2 bg-gray-50 text-xs font-medium text-gray-500 flex justify-between">
                                <span>{filteredSuppliers.length} supplier{filteredSuppliers.length === 1 ? '' : 's'}</span>
                                <span>{filteredSuppliers.reduce((n, s) => n + s.assignments.length, 0)} assignment{filteredSuppliers.reduce((n, s) => n + s.assignments.length, 0) === 1 ? '' : 's'}</span>
                            </div>
                            {filteredSuppliers.map(s => (
                                <SupplierRowItem
                                    key={`${s.CompanyCode}-${s.SupplierDimID}`}
                                    supplier={s}
                                    employees={employees}
                                    canEdit={canEdit}
                                    isAddOpen={openAddFor === s.SupplierDimID}
                                    onToggleAdd={() => setOpenAddFor(prev => prev === s.SupplierDimID ? null : s.SupplierDimID)}
                                    onAdd={handleAdd}
                                    onRemove={handleRemove}
                                    onSetPrimary={handleSetPrimary}
                                    busyKey={busyKey}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

interface SupplierRowItemProps {
    supplier: SupplierWithAssignments;
    employees: EmployeeRow[];
    canEdit: boolean;
    isAddOpen: boolean;
    onToggleAdd: () => void;
    onAdd: (s: SupplierWithAssignments, staffID: string) => void;
    onRemove: (assignmentId: number, label: string) => void;
    onSetPrimary: (s: SupplierWithAssignments, staffID: string) => void;
    busyKey: string | null;
}

function SupplierRowItem({ supplier, employees, canEdit, isAddOpen, onToggleAdd, onAdd, onRemove, onSetPrimary, busyKey }: SupplierRowItemProps) {
    const assignedStaffIds = useMemo(
        () => new Set(supplier.assignments.map(a => a.StaffID)),
        [supplier.assignments]
    );

    return (
        <div className="px-4 py-3 hover:bg-gray-50">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900">
                        {supplier.SupplierDimName || <span className="italic text-gray-400">(no name)</span>}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
                        <span>{supplier.CompanyCode.toUpperCase()} · {supplier.SupplierDimCode}</span>
                        {supplier.businessUnits.map(bu => (
                            <span
                                key={bu}
                                className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200"
                            >
                                {bu}
                            </span>
                        ))}
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-1.5 justify-end max-w-[60%]">
                    {supplier.assignments.map(a => {
                        const removeKey = `remove-${a.AssignmentID}`;
                        const primaryKey = `primary-${supplier.SupplierDimID}-${a.StaffID}`;
                        return (
                            <span
                                key={a.AssignmentID}
                                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${
                                    a.IsPrimary
                                        ? 'bg-blue-100 text-blue-800 ring-1 ring-blue-300'
                                        : 'bg-gray-100 text-gray-700'
                                }`}
                                title={a.JobTitle ?? undefined}
                            >
                                {canEdit && !a.IsPrimary && (
                                    <button
                                        onClick={() => onSetPrimary(supplier, a.StaffID)}
                                        disabled={busyKey === primaryKey}
                                        className="text-gray-400 hover:text-blue-600 disabled:opacity-50"
                                        title="Set as primary"
                                    >
                                        <Star className="h-3 w-3" />
                                    </button>
                                )}
                                {a.IsPrimary && <Star className="h-3 w-3 fill-blue-600 text-blue-600" />}
                                {a.EmployeeName}
                                {canEdit && (
                                    <button
                                        onClick={() => onRemove(a.AssignmentID, a.EmployeeName)}
                                        disabled={busyKey === removeKey}
                                        className="text-gray-400 hover:text-red-600 disabled:opacity-50"
                                        title="Remove assignment"
                                    >
                                        <X className="h-3 w-3" />
                                    </button>
                                )}
                            </span>
                        );
                    })}
                    {canEdit && (
                        <div className="relative">
                            <button
                                onClick={onToggleAdd}
                                className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium border border-dashed border-gray-300 text-gray-600 hover:border-blue-400 hover:text-blue-600"
                            >
                                <UserPlus className="h-3 w-3" />
                                Add
                            </button>
                            {isAddOpen && (
                                <EmployeePicker
                                    employees={employees}
                                    excludeStaffIds={assignedStaffIds}
                                    onPick={staffID => onAdd(supplier, staffID)}
                                    onClose={onToggleAdd}
                                />
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

interface EmployeePickerProps {
    employees: EmployeeRow[];
    excludeStaffIds: Set<string>;
    onPick: (staffID: string) => void;
    onClose: () => void;
}

function EmployeePicker({ employees, excludeStaffIds, onPick, onClose }: EmployeePickerProps) {
    const [filter, setFilter] = useState('');
    const wrapRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [onClose]);

    const candidates = useMemo(() => {
        const term = filter.trim().toLowerCase();
        return employees
            .filter(e => !excludeStaffIds.has(e.StaffID))
            .filter(e => {
                if (!term) return true;
                const name = (e.FullName ?? `${e.LastName ?? ''} ${e.FirstName ?? ''}`).toLowerCase();
                return name.includes(term)
                    || (e.Department ?? '').toLowerCase().includes(term)
                    || (e.JobTitle ?? '').toLowerCase().includes(term);
            })
            .slice(0, 50);
    }, [employees, excludeStaffIds, filter]);

    return (
        <div
            ref={wrapRef}
            className="absolute right-0 top-full mt-1 w-72 bg-white border border-gray-200 rounded-md shadow-lg z-20"
        >
            <div className="p-2 border-b border-gray-100">
                <input
                    type="text"
                    autoFocus
                    value={filter}
                    onChange={e => setFilter(e.target.value)}
                    placeholder="Search employee..."
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
            </div>
            <div className="max-h-64 overflow-y-auto">
                {candidates.length === 0 ? (
                    <div className="px-3 py-3 text-xs text-gray-500 text-center">No employees found.</div>
                ) : (
                    candidates.map(emp => {
                        const name = emp.FullName || `${emp.LastName ?? ''} ${emp.FirstName ?? ''}`.trim() || emp.StaffID;
                        return (
                            <button
                                key={emp.StaffID}
                                onClick={() => onPick(emp.StaffID)}
                                className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm border-b border-gray-50 last:border-0"
                            >
                                <div className="font-medium text-gray-900">{name}</div>
                                {(emp.JobTitle || emp.Department) && (
                                    <div className="text-xs text-gray-500 truncate">
                                        {[emp.JobTitle, emp.Department].filter(Boolean).join(' · ')}
                                    </div>
                                )}
                            </button>
                        );
                    })
                )}
            </div>
        </div>
    );
}
